import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import ConditionsCreditContent from '../components/ConditionsCreditContent';
import SEO, { SEOPages } from '../components/SEO';

/**
 * Page de validation du consentement du conjoint / partenaire enregistré.
 * Le conjoint arrive ici via le lien unique reçu par e-mail.
 *
 * Sécurité : le consentement n'est enregistré QUE par le clic explicite
 * sur « J'accepte les conditions » (POST) — jamais au simple chargement
 * de la page, pour éviter les faux positifs des scanners d'e-mails.
 */
const ConsentementConjoint = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState('loading'); // loading | invalid | pending | accepted | error
  const [consent, setConsent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!token) {
      setState('invalid');
      return;
    }
    axios.get(`${API_BASE_URL}/credit/consentement/${token}`)
      .then((response) => {
        setConsent(response.data);
        setState(response.data.statut === 'valide' ? 'accepted' : 'pending');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/credit/consentement/${token}/accepter`);
      setState('accepted');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setState(error.response?.status === 404 ? 'invalid' : 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-atom-dark pt-24 pb-16 flex items-center justify-center" data-testid="consent-loading">
        <div className="text-center">
          <Loader2 className="animate-spin text-blue-400 mx-auto mb-4" size={40} />
          <p className="text-gray-300">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  if (state === 'invalid') {
    return (
      <div className="min-h-screen bg-atom-dark pt-24 pb-16 flex items-center justify-center" data-testid="consent-invalid">
        <div className="glass p-10 rounded-2xl max-w-md mx-4 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
            <AlertCircle className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Lien invalide</h1>
          <p className="text-gray-300 mb-6">
            Ce lien de consentement est invalide ou a expiré. Si vous pensez qu'il s'agit d'une erreur,
            contactez-nous.
          </p>
          <p className="text-gray-400 text-sm">
            <a href="tel:+41325391627" className="text-blue-400 hover:underline">+41 32 539 16 27</a>
          </p>
        </div>
      </div>
    );
  }

  if (state === 'accepted') {
    return (
      <div className="min-h-screen bg-atom-dark pt-24 pb-16 flex items-center justify-center" data-testid="consent-accepted">
        <div className="glass p-10 rounded-2xl max-w-md mx-4 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
            <CheckCircle className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Consentement enregistré</h1>
          <p className="text-gray-300 mb-2">
            Merci{consent?.conjoint_prenom ? ` ${consent.conjoint_prenom}` : ''} ! Votre consentement aux conditions
            de demande de crédit a bien été enregistré.
          </p>
          {consent?.reference && (
            <p className="text-gray-400 text-sm mb-6">Référence du dossier : <span className="text-blue-400 font-semibold">{consent.reference}</span></p>
          )}
          <p className="text-gray-400 text-sm">
            Aucune autre action n'est requise de votre part. Vous pouvez fermer cette page.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-atom-dark pt-24 pb-16 flex items-center justify-center" data-testid="consent-error">
        <div className="glass p-10 rounded-2xl max-w-md mx-4 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
            <AlertCircle className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Une erreur est survenue</h1>
          <p className="text-gray-300 mb-6">Veuillez réessayer dans quelques instants.</p>
          <button onClick={() => setState('pending')} className="btn-glass btn-primary" data-testid="consent-retry">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // state === 'pending'
  return (
    <div className="min-h-screen bg-atom-dark pt-24 pb-16" data-testid="consent-page">
      <SEO {...SEOPages.consentementConjoint} />
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Bandeau d'information */}
          <div className="glass p-6 rounded-2xl mb-8 border border-blue-500/30">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                <ShieldCheck className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white mb-1">
                  Bonjour{consent?.conjoint_prenom ? ` ${consent.conjoint_prenom}` : ''}, votre consentement est requis
                </h1>
                <p className="text-gray-300 text-sm">
                  {consent?.demandeur || 'Votre conjoint / partenaire'} a déposé une demande de crédit privé
                  {consent?.reference && <> (référence <span className="text-blue-400 font-semibold">{consent.reference}</span>)</>} auprès
                  d'ATOM Group SA. Veuillez lire les conditions ci-dessous puis confirmer votre accord.
                </p>
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div className="glass p-8 md:p-12 rounded-2xl">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
              Mentions légales relatives aux demandes de crédit
            </h2>

            <ConditionsCreditContent />

            {/* Bouton de validation */}
            <div className="mt-12 pt-8 border-t border-gray-700 text-center">
              <p className="text-gray-300 text-sm mb-6">
                En cliquant sur « J'accepte les conditions », vous confirmez avoir lu et accepté
                l'intégralité des conditions ci-dessus. Votre validation sera horodatée et enregistrée.
              </p>
              <button
                onClick={handleAccept}
                disabled={isSubmitting}
                className="btn-glass btn-primary text-lg px-12 py-4 inline-flex items-center disabled:opacity-50"
                data-testid="consent-accept-button"
              >
                {isSubmitting ? (
                  <><Loader2 className="animate-spin mr-2" size={20} />Enregistrement...</>
                ) : (
                  <><CheckCircle className="mr-2" size={20} />J'accepte les conditions</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentementConjoint;
